import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Layout, Button } from 'antd';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveWorkspaceAction, setCollapseLeftBar } from '../../features/organization/organizationSlice';

import Sidebar from '../Sidebar/Sidebar';
import ResizableWrapper from '../ResizeableSlider';
import { useFileOperationsContext } from '../../contexts/FileOperationsContext';
import FileOperationModals from '../FileOperationModals';
import { useLazyFetchSearchResultsQuery } from '../../features/search/searchApi';
import { WORKSPACE_TYPE_MAP } from '../../utils/constants';
import HomePageLeftbar from '../HomePageLeftbar';
import SpaceLeftbar from '../SpaceLeftbar';
import { findValidWorkspace } from '../../utils/common';

const { Sider } = Layout;

/**
 * The main left sidebar component that renders all navigation and file explorer
 */
const Leftbar = ({ activeStaticMenu, popoverMenuContent, routeName, setOpenWorkspaceCreateModal }) => {
  const dispatch = useDispatch();
  const [triggerSearch, searchQueryResult] = useLazyFetchSearchResultsQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('1');
  const [loadedTabs, setLoadedTabs] = useState(['1']);

  // Get file operations from context
  const {
    modalState,
    closeModal,
    handleMoveFile,
    handleCopyFile,
    handleDeleteFile,
    handleDeleteFolder,
    handleRenameFile,
    handleRenameFolder,
    handleMoveFolder,
  } = useFileOperationsContext();

  // Group file actions for passing to FileOperationModals
  const fileActions = useMemo(
    () => ({
      handleDeleteFile,
      handleDeleteFolder,
      handleRenameFile,
      handleRenameFolder,
      handleMoveFile,
      handleCopyFile,
      handleMoveFolder,
    }),
    [
      handleDeleteFile,
      handleDeleteFolder,
      handleRenameFile,
      handleRenameFolder,
      handleMoveFile,
      handleCopyFile,
      handleMoveFolder,
    ]
  );

  // Get state from Redux
  const userPermissions = useSelector((state) => state.user?.userPermissions);
  const collapseLeftBar = useSelector((state) => state.organization?.collapseLeftBar);
  const workspaceList = useSelector((state) => state.organization?.workspaceList);
  const prevRef = useRef({ term: '', type: WORKSPACE_TYPE_MAP[activeTab], page: 0 });
  const workspaceId = useMemo(
    () => findValidWorkspace(workspaceList, userPermissions),
    [workspaceList, userPermissions]
  );

  // Component state
  const [isCollaspse, setIsCollaspse] = useState(true);

  // Fetch folders when workspace ID changes
  useEffect(() => {
    if (workspaceId && activeStaticMenu && activeStaticMenu !== 'space') {
      dispatch(setActiveWorkspaceAction(workspaceId));
    }
  }, [workspaceId, activeStaticMenu, dispatch]);

  // Function to handle tab change
  const handleTabChange = useCallback(
    (key) => {
      setActiveTab(key);
      // Add this tab to our loaded tabs if it's not already there
      if (!loadedTabs.includes(key)) {
        setLoadedTabs((prev) => [...prev, key]);
      }
    },
    [loadedTabs]
  );

  // Close sidebar when resizing starts.
  useEffect(() => {
    const handleResizeStart = () => {
      if (!isCollaspse && !collapseLeftBar) {
        setIsCollaspse(true);
      }
    };

    document.addEventListener('mousedown', (e) => {
      if (e.target.closest('.resize-handle')) {
        handleResizeStart();
      }
    });

    return () => {
      document.removeEventListener('mousedown', handleResizeStart);
    };
  }, [isCollaspse, collapseLeftBar, setIsCollaspse]);

  return (
    <>
      <ResizableWrapper defaultWidth={273}>
        <Layout style={{ position: 'relative' }}>
          <Sider
            width="100%"
            className={`leftbar ${collapseLeftBar ? 'collapseLeftBar' : ''}`}
            theme="light"
            style={{
              borderRight: '1px solid rgba(0, 0, 0, 0.06)',
              background: '#fff',
              overflow: 'hidden',
            }}
            collapsible
            collapsed={collapseLeftBar}
            trigger={null}
          >
            {routeName != 'space' ? (
              <HomePageLeftbar
                setIsCollaspse={setIsCollaspse}
                activeStaticMenu={activeStaticMenu}
                isCollaspse={isCollaspse}
                setOpenWorkspaceCreateModal={setOpenWorkspaceCreateModal}
                popoverMenuContent={popoverMenuContent}
              />
            ) : (
              <SpaceLeftbar setIsCollaspse={setIsCollaspse} popoverMenuContent={popoverMenuContent} />
            )}
          </Sider>
          <Layout>
            <Button
              shape="circle"
              icon={collapseLeftBar ? <RightOutlined /> : <LeftOutlined />}
              onClick={() => {
                dispatch(setCollapseLeftBar(!collapseLeftBar));
                setIsCollaspse(true);
              }}
              className="leftbar-slider"
            />
          </Layout>
        </Layout>
      </ResizableWrapper>
      {!isCollaspse && !collapseLeftBar && (
        <Sidebar
          isCollaspse={isCollaspse}
          setIsCollaspse={setIsCollaspse}
          popoverMenuContent={popoverMenuContent}
          triggerSearch={triggerSearch}
          searchQueryResult={searchQueryResult}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          activeTab={activeTab}
          loadedTabs={loadedTabs}
          handleTabChange={handleTabChange}
          prevRef={prevRef}
          setOpenWorkspaceCreateModal={setOpenWorkspaceCreateModal}
        />
      )}

      {/* File Operation Modals */}
      <FileOperationModals modalState={modalState} closeModal={closeModal} fileActions={fileActions} />
    </>
  );
};

export default Leftbar;
